import {
  tContent,
  tContentCarouselReply,
} from 'src/components/Chat/Chat.types';
import {
  eComponentType,
  eFormatType,
} from 'src/pages/WhatsappApi/WhatsappApi.enum';
import { tCard } from 'src/pages/WhatsappApi/WhatsappApi.types';

/**
 * Read the tapped-card coordinates off an event's content regardless of
 * delivery path (INT-1560): bff-web's REST shape wraps them as `carouselReply`,
 * while the WebSocket `chat_event.inserted` echo relays chats-service's
 * `carousel_reply` verbatim. Never read one key directly — always through here.
 *
 * Same mechanism as getContentFunnelMove (RET-1311). Note that the sibling
 * `carousel` field needs no such accessor: its key is identical in camel and
 * snake, so there is nothing to reconcile there.
 */
export function getContentCarouselReply(
  content: tContent | null | undefined,
): tContentCarouselReply | null {
  return content?.carouselReply ?? content?.carousel_reply ?? null;
}

/** What the quote renders for a tapped card: its body text and its media. */
export type tQuotedCard = {
  text: string | null;
  mediaUrl: string | null;
  mediaFormat: eFormatType | null;
};

/**
 * Resolve the card a reply chose, out of the quoted template's own cards.
 *
 * Returns null — and the caller falls back to the plain text quote — whenever
 * the pairing cannot be made honestly:
 * - the reply named no card (every tap on a carousel delivered before
 *   INT-1560 answers with the button text, and those keep arriving),
 * - the quoted event carries no carousel,
 * - or the index falls outside the cards actually held.
 *
 * That last one is not defensive padding. The index is a POSITION in the sent
 * template's cards[], and the quoted event is resolved separately from the
 * reply — it may be older, or truncated. Trusting the index blindly would
 * quote whatever happens to sit at that slot.
 */
export function resolveQuotedCard(
  quotedContent: tContent | null | undefined,
  cardIndex: number | null | undefined,
): tQuotedCard | null {
  if (cardIndex === null || cardIndex === undefined) return null;
  if (!Number.isInteger(cardIndex) || cardIndex < 0) return null;

  const cards = quotedContent?.carousel?.cards;
  if (!cards?.length || cardIndex >= cards.length) return null;

  const card = readCard(cards[cardIndex]);

  // A card with neither copy nor media has nothing to quote. Falling back to
  // the template body is strictly better than rendering an empty bar.
  if (card.text === null && card.mediaUrl === null) return null;

  return card;
}

function readCard(card: tCard): tQuotedCard {
  const body = card.components?.find(
    (component) => component.type === eComponentType.BODY,
  );
  const header = card.components?.find(
    (component) => component.type === eComponentType.HEADER,
  );

  // header_handle[0] is an http(s) URL or the example object is absent — the
  // producer never puts an opaque Meta upload handle here, which a browser
  // could not render.
  const mediaUrl = header?.example?.header_handle?.[0] ?? null;

  return {
    text: body?.text ?? null,
    mediaUrl,
    mediaFormat: header?.format ?? null,
  };
}
